import { HttpClient, HttpEvent, HttpParams, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { enpointCervinoWeb } from 'src/app/configuration/enpointsCervino';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { LoadPreInvoicingModel } from 'src/app/models/invoicing/load-preinvoicing.models';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { environment } from 'src/environments/environment';
import { RepairModel } from './../../models/maintenance/repair.model';
import { PreInvoicingAlertModel } from 'src/app/models/invoicing/pre-invoicing-alert.models';
import { RepairsImportModel } from 'src/app/models/imports/repairs.import.model';

@Injectable({
  providedIn: 'root'
})
export class RepairService {

  constructor(private _httpClient: HttpClient) { }

  createRepair(repairContractModel: RepairModel): Observable<RepairModel> {
    return this._httpClient.post<RepairModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.repair.CreateRepair}`,
      repairContractModel);
  }

  createRepairsByMaintenanceOrderSuppliesAsync(maintenanceOrderId: number,): Observable<boolean> {
    return this._httpClient.post<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.repair.CreateRepairsByMaintenanceOrderSuppliesAsync}`,
      maintenanceOrderId);
  }

  deleteRepair(repairId: number): Observable<RepairModel> {
    return this._httpClient.delete<RepairModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.repair.DeleteRepair}/${repairId}`);
  }

  deleteSomeRepairs(repairIds: number[]): Observable<boolean> {
    return this._httpClient.put<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.repair.DeleteSomeRepairsAsync}`, repairIds);
  }

  downloadGuideImport(): Observable<HttpEvent<Blob>> {
    return this._httpClient.request(new HttpRequest('GET', `${environment.apiCervinoBackend}${enpointCervinoWeb.repair.DownloadGuideImport}`,
      null, { reportProgress: true, responseType: 'blob' }));
  }

  exportImportErrors(errors: PreInvoicingAlertModel[]): Observable<Blob> {
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.repair.ExportImportErrors}`, errors, {
      responseType: 'blob',
    });
  }

  exportModalConsultation(maintenanceOrderId: number, columnSort: string | null, ascendant: boolean): Observable<Blob> {
    const params = new HttpParams().set('maintenanceOrderId', maintenanceOrderId).set('columnSort', columnSort ?? '').set('ascendant', ascendant.toString());
    return this._httpClient.get(`${environment.apiCervinoBackend}${enpointCervinoWeb.repair.ExportModalConsultation}`, {
      params,
      responseType: 'blob',
    });
  }

  exportGeneralConsultation(filters: ColumnResponseModel[], columnSort: string | null, ascendant: boolean): Observable<Blob> {
    const params = new HttpParams().set('columnSort', columnSort ?? '').set('ascendant', ascendant.toString());
    return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.repair.ExportGeneralConsultation}`, filters, {
      params,
      responseType: 'blob',
    });
  }

  getAllRepair(): Observable<Array<RepairModel>> {
    return this._httpClient.get<Array<RepairModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.repair.GetAllRepair}`);
  }

  getColumnsForGeneralConsultation(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.repair.GetColumnsForGeneralConsultation}`);
  }

  getColumnsForModalConsultation(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.repair.GetColumnsForModalConsultation}`);
  }

  getColumnsForModalDevolution(): Observable<ColumnResponseModel[]> {
    return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.repair.GetColumnsForModalDevolution}`);
  }

  getRepairById(repairId: number): Observable<RepairModel> {
    return this._httpClient.get<RepairModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.repair.GetRepairById}/${repairId}`);
  }

  getRepairsByKeysAsync(maintenanceOrderId: number, articleId: number): Observable<RepairModel> {
    return this._httpClient.get<RepairModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.repair.GetRepairByKeysAsync}/${maintenanceOrderId}/${articleId}`);
  }

  getRapairsDevolutionByKeysAsync(devolutionId: number, articleId: number): Observable<RepairModel> {
    return this._httpClient.get<RepairModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.repair.GetRapairsDevolutionByKeysAsync}/${devolutionId}/${articleId}`);
  }

  getRepairsWithPagination(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<any>> {
    const params = new HttpParams()
      .set('page', page)
      .set('limit', limit)
      .set('columnSort', columnSort || '')
      .set('ascendant', ascendant);

    return this._httpClient.post<PagedResponseModel<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.repair.GetAllRepairsWithPagination}`,
      filters, { params: params }
    );
  }

  getRepairsModalWithPagination(page: number, limit: number, columnSort: string | null, ascendant: boolean, maintenanceOrderId: number): Observable<PagedResponseModel<any>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '')
      .set('ascendant', ascendant).set('maintenanceOrderId', maintenanceOrderId);

    return this._httpClient.get<PagedResponseModel<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.repair.GetAllRepairsModalWithPagination}`,
      { params: params }
    );
  }

  getAllRepairsDevolutionModalWithPagination(page: number, limit: number, columnSort: string | null, ascendant: boolean, devolutionDetailId: number): Observable<PagedResponseModel<any>> {
    const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '')
      .set('ascendant', ascendant).set('devolutionDetailId', devolutionDetailId);

    return this._httpClient.get<PagedResponseModel<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.repair.GetAllRepairsDevolutionModalWithPagination}`,
      { params: params }
    );
  }

  importRepairs(repairsImports: Array<RepairsImportModel>): Observable<LoadPreInvoicingModel> {
    return this._httpClient.post<LoadPreInvoicingModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.repair.ImportRepairsAsync}`,
      repairsImports
    );
  }

  updateRepair(repairContractModel: RepairModel): Observable<RepairModel> {
    return this._httpClient.put<RepairModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.repair.UpdateRepair}`,
      repairContractModel);
  }

}
